import { useMutation, useQueryClient } from '@tanstack/react-query';
import { shareProviderWithClients } from '../api/providerReferralsApi';
import { useAuth } from '@/features/authentication/hooks/useAuth';
import type { ProviderReferralResponse } from '../api/providerReferralsApi';

type ShareProviderMutationData = {
  referredProviderId: string;
  clientIds: string[];
  personalNote?: string;
  profileViewEngagementId?: string;
  // Feature 102: required attestation + client-name opt-out (default on).
  attested: boolean;
  shareClientName?: boolean;
};

/**
 * Hook to share a provider with clients
 * Feature 001: SP Client Management - Provider Referrals
 *
 * @returns Mutation object for sharing providers
 */
export function useShareProvider() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation<ProviderReferralResponse, Error, ShareProviderMutationData>({
    mutationFn: (data: ShareProviderMutationData) => {
      if (!user?.id) {
        throw new Error('User ID is required');
      }
      return shareProviderWithClients(user.id, data);
    },
    onSuccess: (_data, variables) => {
      // Invalidate provider referrals query to refresh "Already referred" state
      queryClient.invalidateQueries({
        queryKey: ['sp', 'referrals', 'provider', variables.referredProviderId],
      });
    },
  });
}
